/**
 * Apresentação dos estados fiscais na interface.
 *
 * Manual de Identidade Visual, seção 10 (Cores de estado fiscal):
 *   "Cor semântica não substitui texto, código ou estado persistido."
 *   "Nunca comunicar situação apenas pela cor.
 *    Sempre usar texto + ícone + código quando houver."
 *
 * Por isso cada estado carrega OBRIGATORIAMENTE um rótulo textual e uma forma
 * de ícone distinta. A cor é o terceiro sinal, nunca o único. O teste em
 * `test/fiscal-state.test.ts` trava essa invariante.
 */

import type { FiscalDocumentState } from '@optefiscal/fiscal-domain';

export type StateTone = 'authorized' | 'info' | 'warning' | 'error' | 'neutral';

/** Formas distinguíveis sem depender de cor. */
export type StateShape = 'check' | 'clock' | 'alert' | 'cross' | 'archive' | 'question';

export interface FiscalStatePresentation {
  readonly tone: StateTone;
  readonly shape: StateShape;
  readonly label: string;
  /** Explica o que o operador deve entender. Aparece como title/aria. */
  readonly meaning: string;
}

export const FISCAL_STATE_PRESENTATION: Readonly<
  Record<FiscalDocumentState, FiscalStatePresentation>
> = Object.freeze({
  draft: {
    tone: 'neutral',
    shape: 'archive',
    label: 'Rascunho',
    meaning: 'Solicitação ainda não pronta. Não transmite.',
  },
  validating: {
    tone: 'info',
    shape: 'clock',
    label: 'Validando',
    meaning: 'Validando contrato e regras antes de consumir numeração.',
  },
  ready_to_sign: {
    tone: 'info',
    shape: 'clock',
    label: 'Pronta para assinar',
    meaning: 'XML fechado. Somente o Fiscal Core assina.',
  },
  signed: {
    tone: 'info',
    shape: 'clock',
    label: 'Assinada',
    meaning: 'XML assinado e preservado no Cofre Fiscal.',
  },
  transmitting: {
    tone: 'info',
    shape: 'clock',
    label: 'Transmitindo',
    meaning: 'Chamada em curso ao autorizador. Tentativa registrada.',
  },
  awaiting_response: {
    tone: 'warning',
    shape: 'question',
    label: 'Aguardando resposta',
    meaning: 'Resposta não conclusiva. Não retransmitir cegamente.',
  },
  authorized: {
    tone: 'authorized',
    shape: 'check',
    label: 'Autorizada',
    meaning: 'Documento oficial imutável.',
  },
  rejected: {
    tone: 'error',
    shape: 'cross',
    label: 'Rejeitada',
    meaning: 'Rejeição oficial do autorizador.',
  },
  unknown_state: {
    tone: 'warning',
    shape: 'question',
    label: 'Estado desconhecido',
    meaning: 'Não há certeza do resultado. Consultar o autorizador antes de qualquer retry.',
  },
  cancel_requested: {
    tone: 'warning',
    shape: 'clock',
    label: 'Cancelamento solicitado',
    meaning: 'Aguardando protocolo do evento de cancelamento.',
  },
  cancelled: {
    tone: 'neutral',
    shape: 'archive',
    label: 'Cancelada',
    meaning: 'Evento autorizado. Documento e evento preservados.',
  },
  failed_terminal: {
    tone: 'error',
    shape: 'alert',
    label: 'Falha terminal',
    meaning: 'Falha local não recuperável. Exige intervenção.',
  },
});

export function presentFiscalState(state: FiscalDocumentState): FiscalStatePresentation {
  return FISCAL_STATE_PRESENTATION[state];
}
